import { useCallback, useEffect } from 'react';

import { parseISO } from 'date-fns';
import { Info } from 'lucide-react';
import { Control, useFieldArray, UseFormSetValue, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { DateRangePicker } from '@/modules/common/components/molecules/date/date-range-picker';
import { DropdownSearchItem } from '@/modules/common/components/molecules/selectors/dropdown';
import { SingleSelect } from '@/modules/common/components/molecules/selectors/single-select/single-select';
import { Dropzone } from '@/modules/common/components/organisms/file/dropzone';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';
import { useUserProfile } from '@/modules/settings/hooks/account/use-user-profile';

import { searchApprovers } from '../../api/time-offs.api';
import { useSuggestedApprovers } from '../../hooks/use-suggested-approvers';
import { useTimeOffTypeOptions } from '../../hooks/use-time-off-type-options';
import { useWorkingDays } from '../../hooks/use-working-days';
import { TimeOffBalancePolicy } from '../../types/time-off-balance';
import { TimeOffDetailsMode } from '../../types/time-off-details-mode';
import { ApprovalStepStatus, DayPortion } from '../../types/time-off-request';
import { TimeOffRequestDetails } from '../../types/time-off-request-details';
import {
  buildDaysFromWorkingDays,
  buildWorkingDaysFromDateRangeFallback,
} from '../../validations/time-off-request-create-schema';
import { buildDaysFromDetailsAndWorkingDays } from '../../validations/time-off-request-details-schema';
import {
  TIME_OFF_FIELD_APPROVAL_REQUIRED,
  TIME_OFF_FIELD_APPROVERS,
  TIME_OFF_FIELD_CATEGORY,
  TIME_OFF_FIELD_DATE_RANGE,
  TIME_OFF_FIELD_DAYS,
  TIME_OFF_FIELD_DESCRIPTION,
  TimeOffRequestFormValues,
} from '../../validations/time-off-request-form.schema';
import { TimeOffApproversTable } from './time-off-approvers-table';
import { TimeOffDayList } from './time-off-day-list';
import { TimeOffRequestAttachmentSection } from './time-off-request-attachment';
import { TimeOffDayListSkeleton } from './time-off-request-skeleton';

interface TimeOffRequestFormProps {
  mode: TimeOffDetailsMode;
  control: Control<TimeOffRequestFormValues>;
  setValue: UseFormSetValue<TimeOffRequestFormValues>;
  /** Present in read/approve modes — drives the category label and attachments. */
  details?: TimeOffRequestDetails;
  /** Whether the selected type requires a supporting document (from the balance policy). */
  requiresDocument?: boolean;
  /** Create mode: the uploaded document file + setter (no upload endpoint yet). */
  documentFile?: File | null;
  onDocumentFileChange?: (file: File | null) => void;
  /** Create mode: the selected type's policy — gates portion options + approvers. */
  policy?: TimeOffBalancePolicy | null;
}

/**
 * Single mode-aware request form. Read/approve render every field disabled and
 * show the approvers' decisions; create renders editable fields, seeds the day
 * rows from the working-days endpoint, and manages approver add/remove. All
 * three modes compose the same section sub-components.
 */
export const TimeOffRequestForm = ({
  mode,
  control,
  setValue,
  details,
  requiresDocument = false,
  documentFile = null,
  onDocumentFileChange,
  policy,
}: TimeOffRequestFormProps) => {
  const { t } = useTranslation('authenticated.timeOff');
  const isCreate = mode === TimeOffDetailsMode.Create;
  const isEdit = mode === TimeOffDetailsMode.Edit;
  const isEditable = isCreate || isEdit;
  const isDisabled = !isEditable;

  // Create mode: the portions the type's policy permits (FULL is always allowed).
  // In read/approve we don't filter (the row shows the request's own value).
  const allowedPortions = isEditable
    ? [
        DayPortion.Full,
        ...(policy?.allowHalfDay ? [DayPortion.Half] : []),
        ...(policy?.allowQuarterDay ? [DayPortion.Quarter] : []),
        ...(policy?.allowHourly ? [DayPortion.Hourly] : []),
      ]
    : undefined;

  // The approvers section is shown only when the type requires approval. Read/
  // approve always show it (a request that exists went through approval setup).
  const showApprovers = !isEditable || (policy?.approvalRequired ?? true);

  // Current user — excluded from the approvers dropdown (you can't approve your own request).
  const { userProfile } = useUserProfile();
  const currentUserId = userProfile?.id;

  const dateRange = useWatch({ control, name: TIME_OFF_FIELD_DATE_RANGE });
  const approvers = useWatch({ control, name: TIME_OFF_FIELD_APPROVERS });

  const { fields: dayFields, replace: replaceDays } = useFieldArray({
    control,
    name: TIME_OFF_FIELD_DAYS,
  });

  // --- Create-only data wiring (queries are disabled outside create mode) ---
  const { data: typeOptions = [] } = useTimeOffTypeOptions(isEditable);
  const suggestedApproversEnabled = isCreate && showApprovers && approvers.length === 0;
  const { data: suggested, isFetching: isSuggestedFetching } =
    useSuggestedApprovers(suggestedApproversEnabled);

  // Skeleton rows while suggested approvers load — the user can still add their
  // own meanwhile; on error there's just nothing to prefill (no error shown).
  const isSuggestedApproversLoading = suggestedApproversEnabled && isSuggestedFetching;

  // Working-days drive the day list. Create uses the picked range; read/approve
  // use the request's own range so weekends render too (the details response only
  // carries the booked days).
  const workingDaysStart = isEditable
    ? dateRange?.from
    : details
      ? parseISO(details.startDate)
      : undefined;
  const workingDaysEnd = isEditable
    ? dateRange?.to
    : details
      ? parseISO(details.endDate)
      : undefined;
  const {
    data: workingDays,
    isFetching: isWorkingDaysFetching,
    isError: isWorkingDaysError,
  } = useWorkingDays(workingDaysStart, workingDaysEnd);

  // Skeleton only on a first fetch for a picked range — not when switching back to
  // an already-cached range.
  const isDayListLoading =
    isEditable && !!workingDaysStart && !!workingDaysEnd && isWorkingDaysFetching && !workingDays;

  // Reseed the day rows when working-days change. On error, fall back to a
  // client-side range (Sat/Sun = non-working) — same shape as the API, so both
  // modes feed it through their normal seeders. Read/approve merge with the
  // booked days (keeping their portions).
  useEffect(() => {
    const resolvedWorkingDays =
      workingDays ??
      (isWorkingDaysError && workingDaysStart && workingDaysEnd
        ? buildWorkingDaysFromDateRangeFallback(workingDaysStart, workingDaysEnd)
        : undefined);
    if (!resolvedWorkingDays) return;

    if (isEditable) {
      replaceDays(buildDaysFromWorkingDays(resolvedWorkingDays, dayFields));
    } else if (details) {
      replaceDays(buildDaysFromDetailsAndWorkingDays(details, resolvedWorkingDays));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditable, workingDays, isWorkingDaysError, details, replaceDays]);

  // When the policy's allowed portions change (e.g. switching to a Full-only
  // type), reset any day whose portion is no longer permitted back to FULL so the
  // locked select and validation stay consistent.
  const allowedPortionsKey = allowedPortions?.join(',') ?? '';
  useEffect(() => {
    if (!isEditable || !allowedPortions) return;
    let changed = false;
    const next = dayFields.map(day => {
      if (day.isWorkingDay && !allowedPortions.includes(day.portion)) {
        changed = true;
        return { ...day, portion: DayPortion.Full, dayPartNumber: '', minutes: undefined };
      }
      return day;
    });
    if (changed) replaceDays(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditable, allowedPortionsKey]);

  // Keep the form's `approvalRequired` flag in sync with the selected type's
  // policy (drives the create-schema approver requirement). When approval isn't
  // required, clear any picked approvers so they aren't submitted.
  useEffect(() => {
    if (!isEditable) return;
    setValue(TIME_OFF_FIELD_APPROVAL_REQUIRED, showApprovers, { shouldValidate: true });
    if (!showApprovers && approvers.length > 0) {
      setValue(TIME_OFF_FIELD_APPROVERS, [], { shouldValidate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditable, showApprovers]);

  // Prefill suggested approvers once (only while none are picked yet, and only
  // when approval is actually required). The current user can't approve their
  // own request, so they're filtered out.
  useEffect(() => {
    if (isCreate && showApprovers && suggested && suggested.length > 0 && approvers.length === 0) {
      setValue(
        TIME_OFF_FIELD_APPROVERS,
        suggested
          .filter(item => Number(item.id) !== currentUserId)
          .map(item => ({
            id: Number(item.id),
            name: item.name,
            profileImageUrl: item.avatarLink ?? null,
            isSuggested: true,
          })),
        { shouldValidate: true }
      );
    }
    // Only react to the suggested list arriving.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggested]);

  const handleAddApprover = (item: DropdownSearchItem) => {
    const id = Number(item.id);
    if (approvers.some(approver => approver.id === id)) return;
    setValue(
      TIME_OFF_FIELD_APPROVERS,
      [
        ...approvers,
        { id, name: item.name, profileImageUrl: item.avatarLink ?? null, isSuggested: false },
      ],
      { shouldValidate: true }
    );
  };

  const handleRemoveApprover = (id: number) => {
    setValue(
      TIME_OFF_FIELD_APPROVERS,
      approvers.filter(approver => approver.id !== id),
      { shouldValidate: true }
    );
  };

  // Reordering changes review order, so it's locked once any approver has
  // already decided. A brand-new request can never have a decision yet; in edit
  // mode the decision only lives on the originally-fetched `details.approvers`
  // (the live form array has no status field), so that's what's checked here.
  const hasDecidedApprover =
    isEdit &&
    (details?.approvers ?? []).some(
      approver =>
        approver.stepStatus === ApprovalStepStatus.Approved ||
        approver.stepStatus === ApprovalStepStatus.Declined
    );
  const canReorderApprovers = isEditable && approvers.length > 1 && !hasDecidedApprover;

  const handleReorderApprovers = (orderedApprovers: TimeOffRequestFormValues['approvers']) => {
    setValue(TIME_OFF_FIELD_APPROVERS, orderedApprovers, { shouldDirty: true });
  };

  // Loader for the "Add approver" dropdown — filters out already-picked approvers
  // AND the current user (you can't approve your own request) client-side (the
  // search endpoint has no exclusion), so a picked approver disappears from the
  // dropdown and reappears once removed. The dropdown resets its cache whenever
  // `excludedItemsCount` (the picked count) changes.
  const pickedApproverIds = approvers.map(approver => approver.id).join(',');
  const loadApprovers = useCallback(
    async (
      searchString?: string,
      cursor?: number | null
    ): Promise<PaginatedResponse<DropdownSearchItem>> => {
      const result = await searchApprovers(searchString, cursor);
      const excluded = new Set(pickedApproverIds ? pickedApproverIds.split(',') : []);
      if (currentUserId != null) excluded.add(String(currentUserId));
      return new PaginatedResponse({
        items: result.items.filter(item => !excluded.has(item.id)),
        pagination: result.pagination,
      });
    },
    [pickedApproverIds, currentUserId]
  );

  // Category: create offers all types; read/approve expose only the request's own.
  const categoryOptions = isEditable
    ? typeOptions
    : details
      ? [{ value: String(details.type.id), label: details.type.displayName }]
      : [];

  return (
    <div className="flex flex-col gap-6">
      {/* Category + Date range on one line. Labels are rendered here (not via the
          controls' own `label` prop) so both columns share identical
          label→control spacing and stay top-aligned. */}
      <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium">{t('details.sections.category')}</span>
          <SingleSelect
            control={control}
            name={TIME_OFF_FIELD_CATEGORY}
            options={categoryOptions}
            placeholder={t('details.create.selectCategory')}
            disabled={isDisabled}
            searchable={false}
            // In read/approve the label must show even before the form value
            // resolves against the options — seed it straight from the request.
            initialSelectedItem={
              isDisabled && details
                ? { value: String(details.type.id), label: details.type.displayName }
                : null
            }
          />
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium">{t('details.sections.dateRange')}</span>
          <DateRangePicker
            control={control}
            name={TIME_OFF_FIELD_DATE_RANGE}
            placeholder={t('details.create.selectDateRange')}
            disabled={isDisabled}
            // Smaller, truncating text so a full range never overflows the card.
            triggerClassName="text-xs sm:text-sm"
          />
        </div>
      </div>

      {/* Duration and part of day. While create-mode working-days load after a
          range/category change, show skeleton rows instead of stale days. */}
      {(dayFields.length > 0 || isDayListLoading) && (
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium">{t('details.sections.durationAndPartOfDay')}</span>
          {isDayListLoading ? (
            <TimeOffDayListSkeleton />
          ) : (
            <TimeOffDayList
              control={control}
              days={dayFields}
              fieldPrefix={index => `days.${index}`}
              disabled={isDisabled}
              allowedPortions={allowedPortions}
            />
          )}
        </div>
      )}

      {/* Approvers — table; create is editable, read/approve show decisions.
          Hidden in create when the type's policy doesn't require approval. */}
      {showApprovers && (
        <div className="flex flex-col gap-2">
          <span className="flex items-center gap-1 text-sm font-medium">
            {t('details.sections.approvers')}
            <SimpleTooltip content={t('details.sections.approversTooltip.content')}>
              <button
                type="button"
                className="text-muted-foreground transition-colors hover:text-foreground"
                aria-label={t('details.sections.approversTooltip.ariaLabel')}
              >
                <Info className="h-3.5 w-3.5" />
              </button>
            </SimpleTooltip>
          </span>
          {isEditable ? (
            <TimeOffApproversTable
              isEditable
              editableApprovers={approvers}
              loadApprovers={loadApprovers}
              onAddApprover={handleAddApprover}
              onRemoveApprover={handleRemoveApprover}
              isLoadingSuggested={isSuggestedApproversLoading}
              canReorderApprovers={canReorderApprovers}
              onReorderApprovers={handleReorderApprovers}
            />
          ) : (
            <TimeOffApproversTable approvers={details?.approvers ?? []} />
          )}
        </div>
      )}

      {/* Description */}
      <FormField
        control={control}
        name={TIME_OFF_FIELD_DESCRIPTION}
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t('details.sections.description')}{' '}
              <span className="text-muted-foreground">{t('details.optional')}</span>
            </FormLabel>
            <FormControl>
              {/* Read-only uses `readOnly` (not `disabled`) so overflowing text
                  stays scrollable; muted look re-applied manually. */}
              <Textarea
                {...field}
                readOnly={isDisabled}
                rows={4}
                placeholder={t('details.descriptionPlaceholder')}
                className={cn(isDisabled && 'cursor-default bg-muted')}
              />
            </FormControl>
          </FormItem>
        )}
      />

      {/* Attachment — only when the time-off type requires a document. Read/approve
          show the uploaded files; create shows a dropzone to upload one. */}
      {requiresDocument && (
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium">{t('details.sections.attachment')}</span>
          {isCreate ? (
            <Dropzone
              file={documentFile}
              setFile={file => onDocumentFileChange?.(file)}
              accept=".pdf,.jpg,.jpeg,.png"
              maxSize={10}
            />
          ) : (
            <TimeOffRequestAttachmentSection attachments={details?.attachments ?? []} />
          )}
        </div>
      )}
    </div>
  );
};
